'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import {
  Download,
  Share2,
  Linkedin,
  Instagram,
  Layers,
  Loader2,
  ImageOff,
  AlertCircle,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface Post {
  id: string;
  title: string;
  caption: string | null;
  image_url: string | null;
  platform: string | null;
  status: string | null;
  created_at: string;
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: 'short', year: 'numeric' });
}

async function downloadImage(imageUrl: string, filename: string) {
  try {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch {
    // Fallback: open in new tab
    window.open(imageUrl, '_blank');
  }
}

interface PostCardProps {
  post: Post;
}

function PostCard({ post }: PostCardProps) {
  const [imgError, setImgError] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);

  const caption = post.caption ?? '';
  const headline = post.title ?? '';
  const imageUrl = post.image_url ?? '';

  const handleDownload = () => {
    if (!imageUrl) return;
    const filename = `distortion-${post.id.slice(0, 8)}.jpg`;
    downloadImage(imageUrl, filename);
  };

  const handleShare = async () => {
    const shareData: ShareData = {
      title: headline,
      text: caption,
    };

    if (imageUrl && navigator.canShare) {
      try {
        const response = await fetch(imageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'distortion-post.jpg', { type: blob.type });
        if (navigator.canShare({ files: [file] })) {
          shareData.files = [file];
        }
      } catch {
        // Proceed without file
      }
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        // User cancelled or share failed
      }
    } else {
      // Fallback: copy caption
      await navigator.clipboard.writeText(caption);
      showCopyFeedback('Geteilt!');
    }
  };

  const handleCopyForPlatform = async (platform: 'LinkedIn' | 'Instagram') => {
    try {
      await navigator.clipboard.writeText(caption);
      showCopyFeedback(`Caption kopiert! Bereit für Distortion auf ${platform}`);
      alert(`Caption kopiert! Ready for Distortion on ${platform}`);
    } catch {
      alert('Kopieren fehlgeschlagen. Bitte manuell kopieren.');
    }
  };

  const showCopyFeedback = (msg: string) => {
    setCopyFeedback(msg);
    setTimeout(() => setCopyFeedback(null), 2500);
  };

  return (
    <article className="punk-card flex flex-col overflow-hidden group">
      {/* Image */}
      <div className="relative w-full aspect-square bg-surface-2 overflow-hidden">
        {imageUrl && !imgError ? (
          <img
            src={imageUrl}
            alt={headline}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2">
            <ImageOff size={28} className="text-muted" />
            <span className="text-xs text-muted-2">Kein Visual</span>
          </div>
        )}
        {/* Date badge */}
        <div className="absolute top-2 left-2">
          <span className="mono text-xs bg-black/70 text-muted px-2 py-1 rounded backdrop-blur-sm">
            {formatDate(post.created_at)}
          </span>
        </div>
        {/* Status badge */}
        {post.status && (
          <div className="absolute top-2 right-2">
            <span className="mono text-xs bg-accent/90 text-black font-700 px-2 py-1 rounded">
              {post.status}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex flex-col flex-1 p-4 gap-3">
        {/* Headline */}
        <h3 className="text-sm font-700 text-white leading-snug line-clamp-2" title={headline}>
          {headline || <span className="text-muted italic">Kein Titel</span>}
        </h3>

        {/* Caption */}
        <p className="text-xs text-muted leading-relaxed line-clamp-3 flex-1" title={caption}>
          {caption || <span className="italic">Keine Caption vorhanden.</span>}
        </p>

        {/* Copy feedback */}
        {copyFeedback && (
          <div className="text-xs text-green-400 mono bg-green-900/20 border border-green-700/40 rounded px-2 py-1.5 fade-in">
            ✓ {copyFeedback}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center gap-2 pt-2 border-t border-border">
          {/* Download */}
          <button
            onClick={handleDownload}
            disabled={!imageUrl}
            title="Bild herunterladen"
            className={`flex items-center justify-center w-8 h-8 rounded border transition-all focus:outline-none focus:ring-2 focus:ring-accent ${
              imageUrl
                ? 'border-border bg-surface text-muted hover:border-accent hover:text-accent' :'border-border/30 bg-surface/30 text-muted/30 cursor-not-allowed'
            }`}
            aria-label="Bild herunterladen"
          >
            <Download size={14} />
          </button>

          {/* Share */}
          <button
            onClick={handleShare}
            title="Teilen"
            className="flex items-center justify-center w-8 h-8 rounded border border-border bg-surface text-muted hover:border-accent hover:text-accent transition-all focus:outline-none focus:ring-2 focus:ring-accent"
            aria-label="Post teilen"
          >
            <Share2 size={14} />
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* LinkedIn */}
          <button
            onClick={() => handleCopyForPlatform('LinkedIn')}
            title="Caption für LinkedIn kopieren"
            className="flex items-center justify-center w-8 h-8 rounded border border-border bg-surface text-muted hover:border-[#0A66C2] hover:text-[#0A66C2] transition-all focus:outline-none focus:ring-2 focus:ring-[#0A66C2]"
            aria-label="Caption für LinkedIn kopieren"
          >
            <Linkedin size={14} />
          </button>

          {/* Instagram */}
          <button
            onClick={() => handleCopyForPlatform('Instagram')}
            title="Caption für Instagram kopieren"
            className="flex items-center justify-center w-8 h-8 rounded border border-border bg-surface text-muted hover:border-[#E1306C] hover:text-[#E1306C] transition-all focus:outline-none focus:ring-2 focus:ring-[#E1306C]"
            aria-label="Caption für Instagram kopieren"
          >
            <Instagram size={14} />
          </button>
        </div>
      </div>
    </article>
  );
}

export default function BulkCreationView() {
  const { user } = useAuth();
  const [posts, setPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setPosts([]);
      setLoading(false);
      return;
    }

    const fetchPosts = async () => {
      try {
        const supabase = createClient();
        const { data, error: fetchError } = await supabase
          .from('reports')
          .select('id, title, caption, image_url, platform, status, created_at')
          .eq('user_id', user.id)
          .eq('format', 'Distortion-Post')
          .order('created_at', { ascending: false });

        if (fetchError) throw fetchError;
        setPosts(data ?? []);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Unbekannter Fehler';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 size={24} className="text-accent animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <AlertCircle size={24} className="text-red-400" />
        <p className="text-red-400 text-sm mono">{error}</p>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 px-6 text-center">
        <div className="w-16 h-16 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center mb-5">
          <Layers size={28} className="text-accent" />
        </div>
        <h2 className="text-white font-700 text-lg mb-2">Noch keine Distortion-Posts</h2>
        <p className="text-muted text-sm max-w-sm leading-relaxed mb-6">
          Starte eine neue Bulk Creation im Content Generator — wähle einen Zeitraum und lass die KI deinen Content-Kalender befüllen.
        </p>
        <Link
          href="/content-generator"
          className="btn-primary"
          aria-label="Zum Content Generator — neue Bulk Creation starten"
        >
          <Layers size={14} />
          Neue Bulk Creation starten
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <p className="text-sm text-muted">
          <span className="text-white font-600">{posts.length}</span> Posts generiert
        </p>
        <Link href="/content-generator" className="btn-ghost py-1.5 px-3 text-xs">
          <Layers size={12} />
          Neue Batch generieren
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {posts.map((post) => (
          <PostCard key={post.id} post={post} />
        ))}
      </div>
    </div>
  );
}
